import { useEffect, useMemo, useRef, useState } from 'react';
import { createObjectUrlFromStoredAsset, getLocalAssetByKey } from '../lib/localAssetStore';
import { deleteProject, duplicateProject, listProjects, loadProject, renameProject, setLastProjectId, type CalendarProjectSummary } from '../lib/projectStorage';
import type { CalendarProject } from '../types/calendarProject';
import { Button } from '../components/ui/Button';
import { Logo } from '../components/ui/Logo';

type ProjectAssetStatus = {
  expected: number;
  available: number;
  missing: number;
  badge: string;
  state: 'ok' | 'missing' | 'empty';
};

type ProjectCardState = {
  summary: CalendarProjectSummary;
  project: CalendarProject | null;
  assetStatus: ProjectAssetStatus;
  previewUrl?: string;
};

const preflightLabels = { not_started: 'Nezkontrolováno', ok: 'OK', warning: 'Upozornění', error: 'Chyby' };

function navigateTo(path: string) {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function getOpenPath(project: CalendarProject | null) {
  if (!project?.productId || !project.period || !project.themeId) return '/start';
  if (project.pages.length > 0) return '/draft';
  return '/upload';
}

function getEditorPath(project: CalendarProject | null) {
  return project?.pages.length ? '/editor' : '/draft';
}

function getPreflightPath(project: CalendarProject | null) {
  return project?.pages.length ? '/preflight' : '/draft';
}

function formatUpdatedAt(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Upraveno neznámo';
  return `Upraveno ${date.toLocaleDateString('cs-CZ', { day: 'numeric', month: 'long', year: 'numeric' })} ${date.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}`;
}

function periodLabel(project: CalendarProject | null) {
  if (!project) return 'Období není dostupné';
  const startMonth = String(project.period.startMonth).padStart(2, '0');
  return `${startMonth}/${project.period.year} · ${project.period.monthsCount} měsíců`;
}

async function resolveAssetStatus(project: CalendarProject | null): Promise<ProjectAssetStatus> {
  if (!project?.photos.length) {
    return { expected: 0, available: 0, missing: 0, badge: 'Bez fotek', state: 'empty' };
  }

  const photosWithLocalKeys = project.photos.filter((photo) => photo.localAssetKey);
  const availability = await Promise.all(photosWithLocalKeys.map(async (photo) => {
    try {
      return Boolean(photo.localAssetKey && await getLocalAssetByKey(photo.localAssetKey));
    } catch {
      return false;
    }
  }));
  const available = availability.filter(Boolean).length;
  const expected = photosWithLocalKeys.length || project.photos.length;
  const missing = Math.max(0, expected - available);

  if (missing > 0) return { expected, available, missing, badge: 'Chybí lokální fotky', state: 'missing' };
  return { expected, available, missing, badge: 'Lokální fotky OK', state: 'ok' };
}

async function resolvePreviewUrl(project: CalendarProject | null) {
  if (!project) return undefined;
  const coverPage = project.pages.find((page) => page.type === 'cover');
  const coverPhoto = project.photos.find((photo) => photo.id === coverPage?.photoId);
  const fallbackPhoto = coverPhoto ?? project.photos.find((photo) => photo.localAssetKey) ?? project.photos[0];
  if (!fallbackPhoto) return undefined;

  try {
    return await createObjectUrlFromStoredAsset(project.id, fallbackPhoto.id, fallbackPhoto.localAssetKey) ?? undefined;
  } catch {
    return undefined;
  }
}

export function ProjectsDashboard() {
  const [projects, setProjects] = useState<ProjectCardState[]>([]);
  const projectsRef = useRef<ProjectCardState[]>([]);
  const [notice, setNotice] = useState('');
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const refreshProjects = async () => {
    const summaries = listProjects();
    const nextProjects = await Promise.all(summaries.map(async (summary) => {
      const project = loadProject(summary.id);
      const [assetStatus, previewUrl] = await Promise.all([resolveAssetStatus(project), resolvePreviewUrl(project)]);
      return { summary, project, assetStatus, previewUrl };
    }));
    setProjects((currentProjects) => {
      currentProjects.forEach((item) => {
        if (item.previewUrl && !nextProjects.some((nextItem) => nextItem.previewUrl === item.previewUrl)) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
      projectsRef.current = nextProjects;
      return nextProjects;
    });
  };

  useEffect(() => {
    void refreshProjects();
    return () => projectsRef.current.forEach((item) => item.previewUrl && URL.revokeObjectURL(item.previewUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = useMemo(() => ({
    total: projects.length,
    drafts: projects.filter((item) => item.summary.preflightStatus !== 'ok').length,
    warnings: projects.filter((item) => item.summary.hasWarnings || item.summary.hasErrors).length,
    assetsOk: projects.filter((item) => item.assetStatus.state === 'ok').length,
  }), [projects]);

  const openProject = (item: ProjectCardState, target: 'open' | 'editor' | 'preflight' | 'order') => {
    setLastProjectId(item.summary.id);
    if (target === 'editor') navigateTo(getEditorPath(item.project));
    else if (target === 'preflight') navigateTo(getPreflightPath(item.project));
    else if (target === 'order') navigateTo(item.project?.pages.length ? '/order' : '/draft');
    else navigateTo(getOpenPath(item.project));
  };

  const duplicate = async (projectId: string) => {
    const duplicatedProject = duplicateProject(projectId);
    if (!duplicatedProject) return;
    setNotice('Projekt zduplikován');
    await refreshProjects();
  };

  const startRename = (item: ProjectCardState) => {
    setRenameId(item.summary.id);
    setRenameValue(item.summary.title);
    setRenameError('');
  };

  const submitRename = async () => {
    if (!renameId) return;
    if (!renameValue.trim()) {
      setRenameError('Název projektu nesmí být prázdný.');
      return;
    }
    renameProject(renameId, renameValue);
    setRenameId(null);
    setRenameValue('');
    setNotice('Projekt přejmenován');
    await refreshProjects();
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    deleteProject(deleteId);
    setDeleteId(null);
    setNotice('Projekt byl smazán. Lokální fotky v IndexedDB zůstaly zachované pro případné kopie.');
    await refreshProjects();
  };

  return (
    <main className="projects-shell">
      <div className="projects-card">
        <header className="projects-header">
          <Logo />
          <nav>
            <a href="/start">Vytvořit kalendář</a>
            <a href="/">Homepage</a>
          </nav>
        </header>

        <section className="projects-hero">
          <p className="eyebrow">PROJECT RECOVERY</p>
          <h1>Moje rozpracované kalendáře</h1>
          <p>Projekty jsou v této MVP verzi uložené pouze v tomto prohlížeči.</p>
          {notice && <div className="projects-notice">{notice}</div>}
        </section>

        {projects.length === 0 ? (
          <section className="projects-empty-state">
            <h2>Zatím tu není žádný rozpracovaný kalendář</h2>
            <p>Začni výběrem produktu a po nahrání fotek se projekt uloží do tohoto prohlížeče.</p>
            <Button href="/start">Vytvořit kalendář</Button>
          </section>
        ) : (
          <>
            <section className="projects-summary" aria-label="Souhrn projektů">
              <article><span>Celkem projektů</span><strong>{stats.total}</strong></article>
              <article><span>Rozpracované</span><strong>{stats.drafts}</strong></article>
              <article><span>S upozorněním</span><strong>{stats.warnings}</strong></article>
              <article><span>Lokální assety OK</span><strong>{stats.assetsOk}</strong></article>
            </section>

            <section className="projects-storage-box">
              <strong>Nahrané fotky zůstávají pouze v tomto prohlížeči</strong>
              <p>Project JSON ukládá metadata. Fotky jsou v MVP v IndexedDB tohoto prohlížeče. Při vymazání dat prohlížeče mohou zmizet. Budoucí verze přidá bezpečný Cloudflare R2 upload.</p>
            </section>

            <section className="projects-grid" aria-label="Rozpracované projekty">
              {projects.map((item) => (
                <article key={item.summary.id} className="project-card">
                  <div className="project-card__preview">
                    {item.previewUrl ? <img src={item.previewUrl} alt={item.summary.title} /> : <span>Calendar Studio</span>}
                    <em className={`asset-badge asset-badge--${item.assetStatus.state}`}>{item.assetStatus.badge}</em>
                  </div>
                  <div className="project-card__body">
                    {renameId === item.summary.id ? (
                      <div className="project-rename">
                        <input value={renameValue} onChange={(event) => setRenameValue(event.target.value)} aria-label="Nový název projektu" />
                        {renameError && <small>{renameError}</small>}
                        <div><button type="button" onClick={() => void submitRename()}>Uložit</button><button type="button" onClick={() => setRenameId(null)}>Zrušit</button></div>
                      </div>
                    ) : (
                      <h2>{item.summary.title}</h2>
                    )}
                    <p>{item.summary.productName} · {periodLabel(item.project)}</p>
                    <dl>
                      <div><dt>Strany</dt><dd>{item.summary.pageCount}</dd></div>
                      <div><dt>Fotky</dt><dd>{item.summary.photoCount} ({item.summary.selectedPhotoCount} vybráno)</dd></div>
                      <div><dt>Preflight</dt><dd>{preflightLabels[item.summary.preflightStatus]}</dd></div>
                      <div><dt>Assety</dt><dd>Assety dostupné: {item.assetStatus.available}/{item.assetStatus.expected}</dd></div>
                    </dl>
                    <span className="project-updated">{formatUpdatedAt(item.summary.updatedAt)}</span>
                    <div className="project-card__actions">
                      <button type="button" onClick={() => openProject(item, 'open')}>Otevřít</button>
                      <button type="button" onClick={() => openProject(item, 'editor')}>Pokračovat v editoru</button>
                      <button type="button" onClick={() => openProject(item, 'preflight')}>Preflight</button>
                      <button type="button" onClick={() => openProject(item, 'order')}>Objednávka</button>
                    </div>
                    <div className="project-card__secondary-actions">
                      <button type="button" onClick={() => void duplicate(item.summary.id)}>Duplikovat</button>
                      <button type="button" onClick={() => startRename(item)}>Přejmenovat</button>
                      <button type="button" onClick={() => setDeleteId(item.summary.id)}>Smazat</button>
                    </div>
                  </div>
                </article>
              ))}
            </section>
          </>
        )}
      </div>

      {deleteId && (
        <div className="projects-modal" role="dialog" aria-modal="true" aria-label="Smazat projekt">
          <div className="projects-modal__card">
            <h2>Smazat projekt?</h2>
            <p>Smaže se projektová metadata. Lokální fotky mohou být použité i jinými kopiemi projektu.</p>
            <div>
              <button type="button" onClick={() => void confirmDelete()}>Smazat projekt</button>
              <button type="button" onClick={() => setDeleteId(null)}>Zrušit</button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
